import {
  createSlice,
  PayloadAction,
  createSelector,
  current,
} from "@reduxjs/toolkit";
import type { CartAddItemPayload } from "./orderToCart";

export interface CartModifierOption {
  id: string;
  displayName: string;
  priceCents: number;
  quantity: number;
  // FOYR-669: out of stock or offline for now. Stays on the line, unpriced,
  // and goes back unchanged on save.
  unavailable?: boolean;
}

export interface CartModifierGroup {
  mod_id: string;
  displayName: string;
  options: CartModifierOption[];
}

export interface CartLineItem {
  lineId: string; // unique key: menuItemId + serialized modifiers
  menuItemId: string;
  name: string;
  priceCentsPerUnit: number; // base item + selected modifiers (per unit)
  quantity: number; // number of main items
  imageUrl?: string;
  caloriesLabel?: string;
  modifiers?: CartModifierGroup[];
  backendItem: BackendCartItem;
  // FOYR-669: out of stock or offline for now. Shown greyed and sent back on
  // every save, so a temporary shortage never removes it from the order.
  unavailable?: boolean;
}

export interface CartState {
  items: CartLineItem[];
  // Fingerprint (orderSourceKey) of the stored order this cart was hydrated
  // from. Absent when the cart holds only lines the guest added on top of
  // nothing - after clearCart, on a first visit, or in state persisted
  // before this field existed. A replacing PUT is only safe for a cart whose
  // key matches the reservation's current order (FOYR-469).
  hydratedFromOrderKey?: string;
}

// "replace": the cart becomes exactly the stored order (page load, Edit Order).
// "reconcile": make sure the cart holds the stored order without losing what
// the guest added ("Add more", the replacing-PUT guard).
export type CartHydrationMode = "replace" | "reconcile";

export interface HydrateFromOrderPayload {
  items: CartAddItemPayload[];
  sourceKey: string;
  mode: CartHydrationMode;
}

// Backend payload structures
export interface BackendModifierPayload {
  optionGroup: { guid: string; name?: string };
  item: { guid: string; name?: string };
  quantity: number;
  modifiers: BackendModifierPayload[];
}

export interface BackendCartItem {
  itemGroup?: { guid: string } | null;
  item: { guid: string; name?: string };
  appliedDiscounts?: any[];
  quantity: number;
  modifiers: BackendModifierPayload[];
}

const initialState: CartState = {
  items: [],
};

/**
 * FOYR-669: a line holding something unavailable. The save keeps it only as
 * stored, so the guest may take it off or take units away, never add units.
 */
export const isLineFrozen = (
  line: Pick<CartLineItem, "unavailable" | "modifiers">,
): boolean =>
  line.unavailable === true ||
  (line.modifiers ?? []).some((group) =>
    group.options.some((option) => option.unavailable === true),
  );

function computeLineId(
  menuItemId: string,
  modifiers?: CartModifierGroup[],
): string {
  const normalized = (modifiers || [])
    .map((g) => ({
      mod_id: g.mod_id,
      options: [...g.options]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((o) => ({ id: o.id, qty: o.quantity })),
    }))
    .sort((a, b) => a.mod_id.localeCompare(b.mod_id));
  const signature = normalized.length > 0 ? JSON.stringify(normalized) : "";
  return `${menuItemId}|${signature}`;
}

/**
 * One unit of a line: the item's base price plus each chosen option's
 * `priceCents` × its quantity. The one rule for the cart and for a stored
 * order shown read-only (orderDisplayLines), so the two can't drift.
 */
export function lineUnitCents(
  basePriceCents: number,
  modifiers: readonly CartModifierGroup[] | undefined,
): number {
  return (modifiers ?? []).reduce(
    (sum, g) =>
      sum + (g.options ?? []).reduce((s, o) => s + o.priceCents * o.quantity, 0),
    basePriceCents,
  );
}

function addLine(state: CartState, payload: CartAddItemPayload): void {
  const {
    menuItemId,
    name,
    basePriceCents,
    quantity,
    imageUrl,
    caloriesLabel,
    modifiers,
    backendItem,
    unavailable,
  } = payload;
  const lineId = computeLineId(menuItemId, modifiers);
  const existing = state.items.find((li) => li.lineId === lineId);
  if (existing) {
    existing.quantity += quantity;
    if (existing.backendItem) existing.backendItem.quantity += quantity;
    return;
  }
  state.items.push({
    lineId,
    menuItemId,
    name,
    priceCentsPerUnit: lineUnitCents(basePriceCents, modifiers),
    imageUrl,
    caloriesLabel,
    quantity,
    modifiers: modifiers && modifiers.length ? modifiers : undefined,
    backendItem: { ...backendItem },
    ...(unavailable ? { unavailable: true } : {}),
  });
}

/** Folds an already-built cart line in, adding to an identical line's quantity. */
function mergeLine(state: CartState, line: CartLineItem): void {
  const existing = state.items.find((li) => li.lineId === line.lineId);
  if (!existing) {
    state.items.push({ ...line, backendItem: { ...line.backendItem } });
    return;
  }
  existing.quantity += line.quantity;
  existing.backendItem.quantity += line.quantity;
}

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    // Ensure state.items exists (migrates old persisted shape on-the-fly)
    _ensureItemsArray(state) {
      const s: any = state as any;
      if (Array.isArray(s.items)) return;
      const itemsById: Record<string, CartLineItem> = s.itemsById || {};
      s.items = Object.values(itemsById);
      delete s.itemsById;
    },
    addItem(
      state,
      action: PayloadAction<{
        menuItemId: string;
        name: string;
        basePriceCents: number;
        quantity: number;
        imageUrl?: string;
        caloriesLabel?: string;
        modifiers?: CartModifierGroup[];
        backendItem: BackendCartItem;
      }>,
    ) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      addLine(state, action.payload);
    },
    // The only way a stored order's items enter the cart (FOYR-469), so the
    // cart always knows which order it holds. See CartHydrationMode.
    hydrateFromOrder(state, action: PayloadAction<HydrateFromOrderPayload>) {
      const { items, sourceKey, mode } = action.payload;
      const heldKey = state.hydratedFromOrderKey;
      // Already holds this order, possibly with unsaved edits: keep them.
      if (mode === "reconcile" && heldKey === sourceKey) return;
      // A cart never hydrated can only hold additions, so they stay on top of
      // the order. One hydrated from an older order is rebuilt without them,
      // since its lines can't be told apart from that order's.
      const additions =
        mode === "reconcile" && heldKey === undefined
          ? current(state).items
          : [];
      state.items = [];
      items.forEach((payload) => addLine(state, payload));
      additions.forEach((line) => mergeLine(state, line));
      state.hydratedFromOrderKey = sourceKey;
    },
    replaceItem(
      state,
      action: PayloadAction<{
        oldLineId: string;
        menuItemId: string;
        name: string;
        basePriceCents: number;
        quantity: number;
        imageUrl?: string;
        caloriesLabel?: string;
        modifiers?: CartModifierGroup[];
        backendItem: BackendCartItem;
      }>,
    ) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const idx = state.items.findIndex(
        (li) => li.lineId === (action as any).payload.oldLineId,
      );
      if (idx === -1) return;
      const {
        menuItemId,
        name,
        basePriceCents,
        quantity,
        imageUrl,
        caloriesLabel,
        modifiers,
        backendItem,
      } = (action as any).payload;
      const unitModifiersCents = (modifiers ?? []).reduce(
        (sum: number, g: CartModifierGroup) =>
          sum +
          (g.options || []).reduce(
            (s: number, o: CartModifierOption) => s + o.priceCents * o.quantity,
            0,
          ),
        0,
      );
      const priceCentsPerUnit = basePriceCents + unitModifiersCents;
      const lineId = computeLineId(menuItemId, modifiers as any);
      (state.items as any)[idx] = {
        lineId,
        menuItemId,
        name,
        priceCentsPerUnit,
        imageUrl,
        caloriesLabel,
        quantity,
        modifiers:
          modifiers && (modifiers as any).length
            ? (modifiers as any)
            : undefined,
        backendItem: { ...(backendItem as any) },
      } as any;
    },
    addOne(state, action: PayloadAction<{ lineId: string }>) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const line = state.items.find(
        (li) => li.lineId === action.payload.lineId,
      );
      if (!line) return;
      (line as any).quantity += 1;
      if ((line as any).backendItem)
        (line as any).backendItem.quantity =
          ((line as any).backendItem.quantity || 0) + 1;
    },
    removeOne(state, action: PayloadAction<{ lineId: string }>) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const idx = state.items.findIndex(
        (li) => li.lineId === action.payload.lineId,
      );
      if (idx === -1) return;
      const line = state.items[idx] as any;
      if (line.quantity <= 1) {
        state.items.splice(idx, 1);
      } else {
        line.quantity -= 1;
        if (line.backendItem)
          line.backendItem.quantity = Math.max(0, (line as any).quantity);
      }
    },
    clearItem(state, action: PayloadAction<{ lineId: string }>) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const idx = state.items.findIndex(
        (li) => li.lineId === action.payload.lineId,
      );
      if (idx !== -1) (state.items as any).splice(idx, 1);
    },
    clearCart(state) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      state.items = [] as any;
      delete state.hydratedFromOrderKey;
    },
  },
});

export const {
  addItem,
  addOne,
  removeOne,
  clearItem,
  clearCart,
  replaceItem,
  hydrateFromOrder,
} = cartSlice.actions;
export default cartSlice.reducer;

export const selectCart = (state: any): CartState => {
  const c: any = state.cart ?? {};
  if (Array.isArray(c.items)) return c as CartState;
  const itemsById: Record<string, CartLineItem> = c.itemsById ?? {};
  return { items: Object.values(itemsById) };
};

export const selectCartItems = createSelector(selectCart, (cart) => cart.items);

export const selectCartCount = createSelector(selectCartItems, (items) =>
  items.reduce((acc, it) => acc + it.quantity, 0),
);

export const selectCartTotalCents = createSelector(selectCartItems, (items) =>
  items.reduce((acc, it) => acc + it.quantity * it.priceCentsPerUnit, 0),
);
